import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  AddRoom,
  ConsolidatorDevice,
  Feature,
  PowerLimitationTooltip,
  RoomCommands,
  RoomProperties,
  VentilationProgramDetails,
} from '../../../projects/client';
import { RemoteDeviceType } from '../commissioning';
import { DeviceTypes } from '../gateway';
import { VentilationQuickSelectionsDetails } from '../installation-details';
import { UpdateFeatureConfig } from '../_models';
import { InstallationFeaturesActions } from './installation-features.actions';
import {
  AddActorConstraints,
  PvStringObject,
  ReplaceGatewayFormData,
  RoomActor,
} from './installation-features.models';
import { InstallationFeaturesSelectors } from './installation-features.selectors';
import { RichDevice } from '../devices';

@Injectable({ providedIn: 'root' })
export class InstallationFeaturesStore {
  constructor(private store: Store) {}

  featuresLoading$ = this.store.select(InstallationFeaturesSelectors.selectFeaturesLoading);
  selectUpdateFeatureLoading$ = this.store.select(
    InstallationFeaturesSelectors.selectUpdateFeatureLoading
  );
  selectFeaturesForAllGatewayLoading$ = this.store.select(
    InstallationFeaturesSelectors.selectFeaturesForAllGatewayLoading
  );
  featuresError$ = this.store.select(InstallationFeaturesSelectors.selectFeaturesError);

  callForHeatHeatingZoneOperatingProgramName$ = this.store.select(
    InstallationFeaturesSelectors.getCallForHeatHeatingZoneOperatingProgramName()
  );
  getHeatingNoiseReductionMode(deviceId?: string, gatewaySerial?: string): Observable<string> {
    return this.store.select(
      InstallationFeaturesSelectors.getHeatingNoiseReductionMode(deviceId, gatewaySerial)
    );
  }

  getFuelVolume$ = this.store.select(InstallationFeaturesSelectors.getFuelVolume());
  getDhwStatus$ = this.store.select(InstallationFeaturesSelectors.selectDhwStatus());
  isVentilationDiagnosticInformationVisible$ = this.store.select(
    InstallationFeaturesSelectors.ventilationDiagnosticInformationVisible()
  );
  isVentilationQuickSelectionsVisible$ = this.store.select(
    InstallationFeaturesSelectors.selectFeaturesForVentilationQuickSelectionsVisibility()
  );
  co2LevelStatus$ = this.store.select(InstallationFeaturesSelectors.getCo2LevelStatus());
  otherRoomsFeature$ = this.store.select(InstallationFeaturesSelectors.getOtherRoomsFeature());
  otherRooms$ = this.store.select(InstallationFeaturesSelectors.getOtherRoomsFeatures());
  rooms$ = this.store.select(InstallationFeaturesSelectors.getRoomsFeatures());
  fixedRooms$ = this.store.select(InstallationFeaturesSelectors.getFixedRoomsFeatures());
  roomsCompatibileWithFhtChannels$ = this.store.select(
    InstallationFeaturesSelectors.getRoomsCompatibileWithFhtChannels()
  );
  roomsAndOtherRoomsNames$ = this.store.select(
    InstallationFeaturesSelectors.getRoomsAndOtherRoomsNames()
  );
  notAssignedHCsToOtherRooms$ = this.store.select(
    InstallationFeaturesSelectors.getNotAssignedHCsToOtherRooms()
  );

  canAddVitotrolToInstallation$: Observable<boolean> = this.store.select(
    InstallationFeaturesSelectors.selectCanAddVitotrolToInstallation()
  );
  unassignedRooms$ = this.store.select(InstallationFeaturesSelectors.getRoomsWithNoActors());
  assignedRooms$ = this.store.select(InstallationFeaturesSelectors.getRoomsWithActors());
  isAnyRoomWithDevice$ = this.store.select(InstallationFeaturesSelectors.isAnyRoomWithDevice());
  isAddRoomDisabled$ = this.store.select(InstallationFeaturesSelectors.isAddRoomDisabled());
  isVentilationSensorReadingVisible$ = this.store.select(
    InstallationFeaturesSelectors.ventilationSensorReadingVisible()
  );

  isFeaturesRequestFinished$ = this.store.select(
    InstallationFeaturesSelectors.selectGetFeaturesRequestFinished
  );

  replaceGatewayFormData$ = this.store.select(
    InstallationFeaturesSelectors.selectReplaceGatewayFormData
  );
  isSelfConsumptionOptimization$ = this.store.select(
    InstallationFeaturesSelectors.selectIsSelfConsumptionOptimization()
  );

  iselectIsHybridProTileVisible(deviceId: string): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.selectIsHybridProTileVisible(deviceId));
  }

  isShowPvVoltage(devices: RichDevice[]): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.selectIsShowPvVoltage(devices));
  }
  getPowerLimitationLabel(deviceId: string): Observable<PowerLimitationTooltip[]> {
    return this.store.select(InstallationFeaturesSelectors.getPowerLimitationLabel(deviceId));
  }

  getAllGatewayFeatures(): void {
    this.store.dispatch(InstallationFeaturesActions.fetchFeatures.attempt());
  }

  /** Should be used to update store values based on websocket live update  */
  upsertManyFeatures(features: Feature<unknown, unknown>[]): void {
    this.store.dispatch(InstallationFeaturesActions.upsertManyFeatures({ features }));
  }

  removeManyFeatures(featureKeys: string[]): void {
    this.store.dispatch(InstallationFeaturesActions.removeManyFeatures({ featureKeys }));
  }

  removeAllFeatures(): void {
    this.store.dispatch(InstallationFeaturesActions.removeAllFeatures());
  }

  setFormReplaceGateway(formData: ReplaceGatewayFormData): void {
    this.store.dispatch(InstallationFeaturesActions.setReplaceGatewayFormData({ formData }));
  }

  /** Designed to select features for any device under this specified gateway
   * @param featureName name of the feature
   * @param deviceId if ommited, currently selected device is used
   * @param gatewaySerial if ommited, currently selected gateway is used
   *
   * @description Example usage selectInstallationDeviceFeature('ems.grid.current.limit', 'HEMS')
   */
  selectInstallationDeviceFeature<TProps, TCommands>(
    featureName: string,
    deviceId?: string,
    gatewaySerial?: string
  ): Observable<Feature<TProps, TCommands>> {
    return this.store.select(
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        featureName,
        deviceId,
        gatewaySerial
      )
    );
  }

  /** Designed to select features from device with role heating under specified gateway
   * @param featureName name of the feature
   * @param gatewaySerial if ommited, currently selected gateway is used
   */
  selectInstallationDeviceFeatureForHeatingDevice<TProps, TCommands>(
    featureName: string,
    gatewaySerial?: string
  ): Observable<Feature<TProps, TCommands>> {
    return this.store.select(
      InstallationFeaturesSelectors.selectInstallationDeviceFeatureForHeatingDevice(
        featureName,
        gatewaySerial
      )
    );
  }

  /** Designed to select features from device of the specified type under specified gateway
   * @param featureName name of the feature
   * @param gatewaySerial if ommited, currently selected gateway is used
   */
  selectInstallationDeviceFeatureByDeviceType<TProps, TCommands>(
    featureName: string,
    deviceType: DeviceTypes,
    gatewaySerial?: string
  ): Observable<Feature<TProps, TCommands>> {
    return this.store.select(
      InstallationFeaturesSelectors.selectInstallationDeviceFeatureByDeviceType(
        featureName,
        deviceType,
        gatewaySerial
      )
    );
  }

  /** Designed to select features for the specified gateway
   * @param featureName name of the feature
   * @param gatewaySerial if ommited, currently selected gateway is used
   *
   * @description Example usage selectInstallationGatewayFeature('gateway.status')
   */
  selectInstallationGatewayFeature<TProps, TCommands>(
    featureName: string,
    gatewaySerial?: string
  ): Observable<Feature<TProps, TCommands>> {
    return this.store.select(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(featureName, gatewaySerial)
    );
  }

  selectInstallationTCUFeature<TProps, TCommands>(
    featureName: string,
    gatewaySerial?: string
  ): Observable<Feature<TProps, TCommands>> {
    return this.store.select(
      InstallationFeaturesSelectors.selectInstallationTCUFeature(featureName, gatewaySerial)
    );
  }

  selectInstallationGatewayFeatureByDeviceId<TProps, TCommands>(
    featureName: string,
    deviceId: string,
    gatewaySerial?: string
  ): Observable<Feature<TProps, TCommands>> {
    return this.store.select(
      InstallationFeaturesSelectors.selectInstallationGatewayFeatureByDeviceId(
        featureName,
        deviceId,
        gatewaySerial
      )
    );
  }

  /** Designed to select features for the hems gateway, under device id HEMS
   * @param featureName name of the feature
   *
   */
  selectInstallationHemsFeature<TProps, TCommands>(
    featureName: string
  ): Observable<Feature<TProps, TCommands>> {
    return this.store.select(
      InstallationFeaturesSelectors.selectInstallationHemsFeature(featureName)
    );
  }

  selectHemsFeaturesPresent$ = this.store.select(
    InstallationFeaturesSelectors.selectHemsFeaturesPresent
  );

  selectRemoteDeviceType(featureName: string, deviceId: string): Observable<string> {
    return this.store.select(
      InstallationFeaturesSelectors.selectRemoteDeviceType(featureName, deviceId)
    );
  }
  /** Updates feature value on the backend  */
  updateFeature(
    featureName: string,
    value: unknown,
    propertyName: string | string[],
    paramName: string | string[],
    commandName?: string,
    gatewaySerial?: string,
    deviceId?: string,
    config?: UpdateFeatureConfig
  ): void {
    this.store.dispatch(
      InstallationFeaturesActions.updateFeature.attempt({
        featureName,
        value,
        propertyName,
        paramName,
        commandName,
        gatewaySerial,
        deviceId,
        config,
      })
    );
  }

  updateMultipleFeatures(
    features: {
      featureName: string;
      value: unknown;
      propertyName: string | string[];
      paramName: string | string[];
      commandName?: string;
      gatewaySerial?: string;
      deviceId?: string | null;
      config?: UpdateFeatureConfig;
    }[]
  ): void {
    this.store.dispatch(InstallationFeaturesActions.updateMultipleFeatures.attempt({ features }));
  }

  getMockedFeature(path: string, featureName: string): void {
    this.store.dispatch(InstallationFeaturesActions.getMockedFeature({ path, featureName }));
  }

  getCircuitHeatingZoneOperatingProgramName(circuitId: string): Observable<string> {
    return this.store.select(
      InstallationFeaturesSelectors.getCircuitHeatingZoneOperatingProgramName(circuitId)
    );
  }

  getVentilationQuickSelectionsDetails(): Observable<VentilationQuickSelectionsDetails> {
    return this.store.select(InstallationFeaturesSelectors.getVentilationQuickSelectionsDetails());
  }

  isAnySpfFeatureReady(): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.isAnySpfFeatureReady());
  }

  shouldDispalyHeatingPanel(circuitId: string): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.shouldDispalyHeatingPanel(circuitId));
  }

  isMaintenanceInformationMenuItemVisible(): Observable<boolean> {
    return this.store.select(
      InstallationFeaturesSelectors.isServiceTimeBasedOrBurnerBasedAvailable()
    );
  }

  getVentilationProgramsDetails(): Observable<VentilationProgramDetails[]> {
    return this.store.select(InstallationFeaturesSelectors.getVentilationProgramsDetails());
  }

  isVicareThermostatMode(): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.isVicareThermostatMode());
  }

  getOffsetDeviceTime(): Observable<Date> {
    return this.store.select(InstallationFeaturesSelectors.getOffsetDeviceTime());
  }

  isSrcMenuItemVisible(): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.srcMenuItemVisible());
  }

  isLocalVsDeviceTimeMismatch(): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.selectIsLocalVsDeviceTimeMismatch());
  }

  isHolidayModeMenuItemVisible(firstEnabledCircuitId: string): Observable<boolean> {
    return this.store.select(
      InstallationFeaturesSelectors.holidayModeMenuItemVisible(firstEnabledCircuitId)
    );
  }

  isControlUnitsMenuItemVisible(boilerSerial: string): Observable<boolean> {
    return this.store.select(
      InstallationFeaturesSelectors.controlUnitsMenuItemVisible(boilerSerial)
    );
  }

  isControlMenuVisible(firstEnabledCircuitId: string): Observable<boolean> {
    return this.store.select(
      InstallationFeaturesSelectors.controlMenuVisible(firstEnabledCircuitId)
    );
  }

  isInstallationDetailsMenuVisible(
    gatewaySerial: string,
    boilerSerial: string
  ): Observable<boolean> {
    return this.store.select(
      InstallationFeaturesSelectors.installationDetailsMenuVisible(gatewaySerial, boilerSerial)
    );
  }

  isTheOnlyAssignedDevice(deviceId: string): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.isTheOnlyAssignedDevice(deviceId));
  }

  isTheOnlyRoomWithDevices(room: Feature<RoomProperties, RoomCommands>): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.isTheOnlyRoomWithDevices(room));
  }

  getDeviceRoom(deviceId: string): Observable<Feature<RoomProperties, RoomCommands>> {
    return this.store.select(InstallationFeaturesSelectors.getDeviceRoom(deviceId));
  }

  canRemoveClimateAndCo2SensorFromRoom(deviceId: string): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.canRemoveClimateSensor(deviceId));
  }

  canDeleteClimateSensor(deviceId: string): Observable<boolean> {
    return this.store.select(InstallationFeaturesSelectors.canDeleteClimateSensor(deviceId));
  }

  getAddActorConstraints(
    roomFeature: Feature<RoomProperties, RoomCommands>,
    deviceId: string
  ): Observable<AddActorConstraints> {
    return this.store.select(
      InstallationFeaturesSelectors.getAddActorConstraints(roomFeature, deviceId)
    );
  }

  isDeviceAssigned(deviceId: string): Observable<{ assigned: boolean }> {
    return this.store.select(InstallationFeaturesSelectors.isDeviceAssigned(deviceId));
  }

  selectConsolidatedValues(
    featureName: string,
    deviceId: string,
    consolidatedDeviceId: string
  ): Observable<ConsolidatorDevice> {
    return this.store.select(
      InstallationFeaturesSelectors.selectConsolidatedValues(
        featureName,
        deviceId,
        consolidatedDeviceId
      )
    );
  }

  getDevicesOfChosenTypeAssignedToTheRoom(
    roomId: string,
    type: RemoteDeviceType
  ): Observable<RoomActor[]> {
    return this.store.select(
      InstallationFeaturesSelectors.getDevicesOfChosenTypeAssignedToTheRoom(roomId, type)
    );
  }

  getPVString(pvStringId: string): Observable<PvStringObject> {
    return this.store.select(InstallationFeaturesSelectors.getPVString(pvStringId));
  }

  updateRooms(rooms: Feature<RoomProperties, RoomCommands>[]): void {
    this.store.dispatch(InstallationFeaturesActions.updateRooms({ rooms }));
  }

  updateAddRoomInfo(room: AddRoom): void {
    this.store.dispatch(InstallationFeaturesActions.updateAddRoomInfo({ room }));
  }

  updateReplaceGateway(): void {
    this.store.dispatch(InstallationFeaturesActions.updateReplaceGateway.attempt());
  }

  selectAllInstallationDeviceRemoteFeatures<
    VitotrolRemoteControllerProperties,
    VitotrolRemoteControllerCommands
  >(
    featureNames: string | RegExp,
    deviceId?: string,
    isHeatingCircuit?: boolean
  ): Observable<Feature<VitotrolRemoteControllerProperties, VitotrolRemoteControllerCommands>[]> {
    return this.store.select(
      InstallationFeaturesSelectors.selectAllInstallationDeviceRemoteFeatures(
        featureNames,
        deviceId,
        isHeatingCircuit
      )
    );
  }
}
